import {useState, useCallback, useMemo} from 'react';
import {format, parse, parseISO} from 'date-fns';

import type {
	BillData,
	ObrigacaoPendente,
	PostCheckoutPaymentParams,
	SaoLucasBillItem,
} from 'domain/usecases';
import {CheckoutRecurrenceEnum, RecurrenceTypeEnum, SubChannelEnum} from 'domain/usecases';

import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';

import {
	CORPORATE_BY_PRODUCT,
	FeaturesEnum,
	X_CONTEXT_OPERADORA_BY_PRODUCT,
	XContextTipoPlanoEnum,
} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useHostName} from 'presentation/hooks/useHostName';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import type {OpenBillRow, OpenBillStatus} from '../../components/OpenBillsTable/types';
import {OpenBillStatusEnum} from '../../components/OpenBillsTable/types';
import {usePostCheckoutPaymentMutation} from '../../mutation/usePostCheckoutPaymentMutation';
import {usePostCheckoutRecurrenceMutation} from '../../mutation/usePostCheckoutRecurrenceMutation';
import {useGetOpenObligationListQuery} from '../../queries/useGetOpenObligationListQuery';
import {useGetInvoicesQuery} from '../../queries/useGetInvoicesQuery';
import {useGetSaoLucasBillsQuery} from '../../queries/useGetSaoLucasBillsQuery';
import {useOpenBillsPage} from '../useOpenBillsPage';
import type {UseOpenBillsActionsReturn} from './types';
import {removeDecimalPoint} from '../../utils/removeDecimalPoint';
import {usePersonalData} from 'presentation/hooks/usePersonalData';
import {translations} from 'presentation/translations-new';

const {invoicePayment} = translations['pt-br'].privateFlow.myFinancial.openBills;

function parseVencimento(vencimento: string): Date {
	const [day, month, year] = vencimento.split('/').map(Number);
	return new Date(year, month - 1, day);
}

function parseVlObrigacao(value: string | number): number {
	if (typeof value === 'number') return value;
	if (value.includes(',')) {
		return parseFloat(value.replace(/\./g, '').replace(',', '.'));
	}
	return parseFloat(value) || 0;
}

function resolveObrigacaoStatus(qtdDiasAtraso: number): OpenBillStatus {
	if (qtdDiasAtraso > 0) return OpenBillStatusEnum.OVERDUE;
	return OpenBillStatusEnum.OPEN;
}

export function mapObrigacaoPendenteToOpenBillRow(
	obrigacao: ObrigacaoPendente,
	index: number,
	nmTitularContrato = '',
): OpenBillRow {
	const status = resolveObrigacaoStatus(obrigacao.qtdDiasAtraso);
	const vlObrigacao = parseVlObrigacao(obrigacao.vlObrigacao);
	const vlAtualizado = parseVlObrigacao(obrigacao.vlAtualizado) || vlObrigacao;
	const beneficiariosMapped = obrigacao.beneficiarios
		.map(item => ({
			nome: item.nmSocial ?? item.nmPessoaRazaoSocial,
			mensalidade: item.vlMensalidade,
			coparticipacao: item.vlCoparticipacao,
			isTitular: item.nmPessoaRazaoSocial === nmTitularContrato,
		}))
		.sort((a, b) => Number(b.isTitular) - Number(a.isTitular));
	return {
		cdObrigacao: String(obrigacao.cdObrigacao || index + 1),
		cdEspecieTitulo: obrigacao.cdEspecieTitulo,
		status,
		vlObrigacao,
		vlAtualizado,
		dtVencimento: obrigacao.dtVencimento,
		dtPrevista: obrigacao.dtPrevista,
		competencia: `${obrigacao.dtCompInicio} à ${obrigacao.dtCompFim}`,
		coparticipacao: beneficiariosMapped,
		baseYear: parse(obrigacao.dtVencimento, 'dd/MM/yyyy', new Date()).getFullYear(),
		beneficiarios: beneficiariosMapped.length,
		cdDocumentoGerador: obrigacao.cdDocumentoGerador,
		digitableLine: obrigacao.cdBarra ?? undefined,
		extraParams: {
			interestAmount: obrigacao.vlJuros,
			fineAmount: obrigacao.vlMulta,
			discountAmount: obrigacao.vlDesconto,
		},
	};
}

function mapBillDataToOpenBillRow(bill: BillData, index: number): OpenBillRow {
	const daysOverdue = bill.linhaDigitavel?.qtDiasAtraso ?? 0;
	const status: OpenBillStatus =
		daysOverdue > 0 ? OpenBillStatusEnum.OVERDUE : OpenBillStatusEnum.OPEN;
	const valor = parseVlObrigacao(bill.linhaDigitavel?.vlObrigacao ?? '0');
	const dtVencimento = bill.linhaDigitavel?.dtVencimento ?? bill.dtPrevista;
	return {
		cdObrigacao: String(bill.cdObrigacao || index + 1),
		cdEspecieTitulo: bill.cdEspecieTitulo,
		status,
		vlObrigacao: valor,
		dtVencimento,
		dtPrevista: bill.dtPrevista,
		competencia: bill.dtCompInicio,
		coparticipacao: [],
		baseYear: parse(dtVencimento, 'dd/MM/yyyy', new Date()).getFullYear(),
		beneficiarios: 0,
		cdDocumentoGerador: bill.linhaDigitavel?.cdBarra ?? undefined,
		digitableLine: undefined,
		vlAtualizado: valor,
	};
}

function mapSaoLucasBillItemToOpenBillRow(
	item: SaoLucasBillItem,
	saoLucasDownloadEnabled: boolean,
): OpenBillRow {
	const dtVencimento = format(parseISO(item.vencimento), 'dd/MM/yyyy');
	const status: OpenBillStatus =
		item.status === 'À Vencer' ? OpenBillStatusEnum.OPEN : OpenBillStatusEnum.OVERDUE;
	return {
		cdObrigacao: String(parseISO(item.vencimento).getTime()),
		cdEspecieTitulo: 0,
		status,
		vlObrigacao: item.valor,
		vlAtualizado: item.valor,
		dtVencimento,
		dtPrevista: dtVencimento,
		isSaoLucas: true,
		downloadLink: saoLucasDownloadEnabled ? item.downloadLink : undefined,
	};
}

function buildMergedRows(
	rows: OpenBillRow[],
	saoLucasBillItems: SaoLucasBillItem[],
	saoLucasDownloadEnabled: boolean,
): OpenBillRow[] {
	const toDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');
	const saoLucasMap = new Map<string, SaoLucasBillItem>();
	for (const item of saoLucasBillItems) {
		saoLucasMap.set(toDateKey(parseISO(item.vencimento)), item);
		saoLucasMap.set(toDateKey(parseISO(item.vencimentoOriginal)), item);
	}
	const usedItems = new Set<SaoLucasBillItem>();
	const result: {row: OpenBillRow; date: Date}[] = [];
	for (const row of rows) {
		const keys = [
			toDateKey(parse(row.dtPrevista, 'dd/MM/yyyy', new Date())),
			toDateKey(parse(row.dtVencimento, 'dd/MM/yyyy', new Date())),
		];
		let matched: SaoLucasBillItem | undefined;
		for (const key of keys) {
			if (saoLucasMap.has(key)) {
				matched = saoLucasMap.get(key)!;
				break;
			}
		}
		const date = parse(row.dtPrevista, 'dd/MM/yyyy', new Date());
		if (matched) {
			usedItems.add(matched);
			result.push({
				row: {
					...row,
					downloadLink: saoLucasDownloadEnabled ? matched.downloadLink : undefined,
				},
				date,
			});
		} else {
			result.push({row, date});
		}
	}
	for (const item of saoLucasBillItems) {
		if (!usedItems.has(item)) {
			result.push({
				row: mapSaoLucasBillItemToOpenBillRow(item, saoLucasDownloadEnabled),
				date: parseISO(item.vencimento),
			});
		}
	}
	result.sort((a, b) => a.date.getTime() - b.date.getTime());
	return result.map(({row}) => row);
}

function getOldestOverdueBill(rows: OpenBillRow[]): OpenBillRow | null {
	const overdue = rows.filter(r => r.status === OpenBillStatusEnum.OVERDUE);
	if (!overdue.length) return null;
	return overdue.reduce((oldest, current) =>
		parseVencimento(current.dtVencimento) < parseVencimento(oldest.dtVencimento)
			? current
			: oldest,
	);
}

export const useOpenBillsActions = (): UseOpenBillsActionsReturn => {
	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isEnabled: isRecurrenceFlag} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_RECURRENCE,
	);
	const {isEnabled: isRecurrencePPOFlag} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_RECURRENCE_PPO,
	);
	const {isEnabled: saoLucasFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BILLS_SAO_LUCAS,
	);
	const {isEnabled: saoLucasDownloadEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_DOWNLOAD_BILLS_SAO_LUCAS,
	);

	const {accountData} = useAccountDataStore();
	const {currentEmail, currentPhone} = usePersonalData();
	const {dataHostNameState, isGndiSaoPaulo} = useHostName();
	const {odontoOnly, isPPOPlan} = useAccountInformation();
	const isRecurrenceEnabled = isRecurrenceFlag;
	const isGndiSpPPORestricted = isGndiSaoPaulo && isPPOPlan && isRecurrencePPOFlag;
	const beneficiary = accountData?.beneficiary;
	const {credentialCodeSaoLucas, flSaoLucasSaude} = accountData?.accesses ?? {};

	const saoLucasEnabled =
		newBillsFlagIsEnabled &&
		saoLucasFlagIsEnabled &&
		!!flSaoLucasSaude &&
		!!credentialCodeSaoLucas;

	const {
		data: billsData,
		isLoading: isLoadingV1,
		error: queryErrorV1,
		refetch: refetchV1,
	} = useGetOpenObligationListQuery(!newBillsFlagIsEnabled);

	const {
		data: invoicesData,
		isLoading: isLoadingInvoices,
		error: queryErrorInvoices,
		refetch: refetchInvoices,
	} = useGetInvoicesQuery(newBillsFlagIsEnabled);

	const {data: saoLucasBills, isLoading: isSaoLucasLoading} = useGetSaoLucasBillsQuery(
		saoLucasEnabled,
		{
			userCode: credentialCodeSaoLucas ?? '',
			birthDate: beneficiary?.dtNascimentoUsuarioC ?? '',
		},
	);

	const isRecurrenceEnabledAndHealth =
		isRecurrenceEnabled &&
		!odontoOnly &&
		!invoicesData?.flDebitoAutomatico &&
		!isGndiSpPPORestricted;

	const isLoading = newBillsFlagIsEnabled
		? isLoadingInvoices || (saoLucasEnabled && isSaoLucasLoading)
		: isLoadingV1;
	const queryError = newBillsFlagIsEnabled ? queryErrorInvoices : queryErrorV1;
	const refetch = newBillsFlagIsEnabled ? refetchInvoices : refetchV1;

	const getAutoPaymentType = () => {
		if (invoicesData?.flDebitoAutomatico) {
			return RecurrenceTypeEnum.DEBIT;
		}
		if (invoicesData?.flRecorrencia) {
			if (invoicesData.tipoRecorrencia === RecurrenceTypeEnum.PIX) {
				return RecurrenceTypeEnum.BILL;
			}
			return invoicesData?.tipoRecorrencia ?? RecurrenceTypeEnum.BILL;
		}
		return RecurrenceTypeEnum.BILL;
	};

	const flRecorrencia = invoicesData?.flRecorrencia ?? false;
	const autoPaymentType = getAutoPaymentType();

	const nmTitularContrato = accountData?.sidebar?.nmTitularContrato ?? '';

	const rows: OpenBillRow[] = useMemo(() => {
		const baseRows = newBillsFlagIsEnabled
			? (invoicesData?.obrigacoesPendentes ?? []).map((obrigacao, index) =>
					mapObrigacaoPendenteToOpenBillRow(obrigacao, index, nmTitularContrato),
				)
			: (billsData ?? []).map(mapBillDataToOpenBillRow);
		if (newBillsFlagIsEnabled && saoLucasEnabled && saoLucasBills?.length) {
			return buildMergedRows(baseRows, saoLucasBills, saoLucasDownloadEnabled);
		}
		return [...baseRows].sort(
			(a, b) =>
				parseVencimento(a.dtVencimento).getTime() -
				parseVencimento(b.dtVencimento).getTime(),
		);
	}, [
		billsData,
		invoicesData,
		newBillsFlagIsEnabled,
		saoLucasEnabled,
		saoLucasBills,
		saoLucasDownloadEnabled,
		nmTitularContrato,
	]);

	const {
		isMobile,
		handleSelectionChange,
		handleToggleRow,
		handleViewDetails,
		handleCloseDrawer,
		tabs,
		drawerOpen,
		selectedRow,
		selectedIds,
		hasPendingInitialDrawer,
	} = useOpenBillsPage(rows);

	const {mutate: checkoutPayment, isPending: isPayLoading} = usePostCheckoutPaymentMutation();
	const {mutate: checkoutRecurrence, isPending: isAutoPaymentLoading} =
		usePostCheckoutRecurrenceMutation();
	const isError = !!queryError;
	const [isPayError, setIsPayError] = useState(false);
	const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
	const [isIframeOpen, setIsIframeOpen] = useState(false);
	const [recurrenceUrl, setRecurrenceUrl] = useState<string | null>(null);
	const [isRecurrenceIframeOpen, setIsRecurrenceIframeOpen] = useState(false);

	const handleSetIsRecurrenceIframeOpen = useCallback(
		(value: boolean) => {
			setIsRecurrenceIframeOpen(value);
			if (!value) {
				refetchInvoices();
			}
		},
		[refetchInvoices],
	);
	const [noSelectionModalOpen, setNoSelectionModalOpen] = useState(false);
	const [olderBillsModalOpen, setOlderBillsModalOpen] = useState(false);
	const [olderBillsCurrentRow, setOlderBillsCurrentRow] = useState<OpenBillRow | null>(null);
	const [isMultiSelectionOlderBills, setIsMultiSelectionOlderBills] = useState(false);

	const oldestOverdueBill = getOldestOverdueBill(rows);
	const numberOfOverdueBills = olderBillsCurrentRow
		? rows.filter(
				r =>
					r.status === OpenBillStatusEnum.OVERDUE &&
					parseVencimento(r.dtVencimento) <
						parseVencimento(olderBillsCurrentRow.dtVencimento),
			).length
		: rows.filter(r => r.status === OpenBillStatusEnum.OVERDUE).length;
	const isEmpty = !isLoading && !isError && rows.length === 0;

	const xContextOperadora = X_CONTEXT_OPERADORA_BY_PRODUCT[dataHostNameState.product];
	const corporate = CORPORATE_BY_PRODUCT[dataHostNameState.product];
	const xContextTipoPlano = odontoOnly
		? XContextTipoPlanoEnum.ODONTO
		: XContextTipoPlanoEnum.SAUDE_OU_ODONTO;
	const xContext = `${xContextOperadora}${xContextTipoPlano}`;

	const openOlderBillsModal = useCallback((currentRow: OpenBillRow, isMulti = false) => {
		setOlderBillsCurrentRow(currentRow);
		setIsMultiSelectionOlderBills(isMulti);
		setOlderBillsModalOpen(true);
	}, []);

	const rawPhoneDigits = (invoicesData?.telefone ?? '').replace(/\D/g, '');
	const normalizedPhone = rawPhoneDigits.startsWith('55')
		? rawPhoneDigits
		: `55${rawPhoneDigits}`;
	const contactNumber = normalizedPhone.length > 12 ? normalizedPhone : '5511999999999';

	const executeAutoPayment = useCallback(
		(onError?: () => void) => {
			const payload = {
				customer: {
					name: beneficiary?.nmUsuario,
					email: currentEmail,
					unit: beneficiary?.nuContrato ?? '',
					documentNumber: beneficiary?.nuCpf ?? '',
					documentType: 'CPF',
				},
				recurrence: {
					items: [
						{
							partnerAccountId: beneficiary?.nuContrato ?? '',
							personId: beneficiary?.nuCpf ?? '',
							name: beneficiary?.nmUsuario ?? '',
							contactNumber,
							email: currentEmail ?? '',
							description: CheckoutRecurrenceEnum.DESCRIPTION,
							extraParams: {
								serviceName: beneficiary?.nmPlano ?? '',
								serviceType: CheckoutRecurrenceEnum.SERVICE_TYPE,
								subchannel: SubChannelEnum.PORTAL_DO_BENEFICIARIO,
								person_id_type: 'CPF',
							},
						},
					],
				},
				'x-context': xContext,
			};

			checkoutRecurrence(payload, {
				onSuccess: data => {
					setRecurrenceUrl(data.content.url);
					setIsRecurrenceIframeOpen(true);
				},
				onError: onError ?? (() => setIsPayError(true)),
			});
		},
		[checkoutRecurrence, beneficiary, currentEmail, invoicesData, xContext],
	);

	const handleAutoPaymentEnable = useCallback(() => {
		executeAutoPayment();
	}, [executeAutoPayment]);

	const handleAutoPaymentChange = useCallback(() => {
		executeAutoPayment();
	}, [executeAutoPayment]);

	const handleAutoPaymentDeactivate = useCallback(() => {
		executeAutoPayment();
	}, [executeAutoPayment]);

	const executePay = useCallback(
		(overrideRows?: OpenBillRow[], onSettled?: () => void) => {
			const selectedRows = overrideRows ?? rows.filter(r => selectedIds.has(r.cdObrigacao));
			const isSinglePayment = selectedRows.length === 1;
			const payload: PostCheckoutPaymentParams = {
				customer: {
					name: beneficiary?.nmUsuario,
					email: currentEmail,
					unit: beneficiary?.nuContrato,
					phoneNumbers: currentPhone
						? [
								{
									ddi: '55',
									ddd: currentPhone.slice(0, 2),
									number: currentPhone.slice(2),
								},
							]
						: [],
					documentNumber: beneficiary?.nuCpfC,
					documentType: 'CPF',
				},
				paymentItems: selectedRows.map((row, index) => {
					const shouldIncludeDigitableLine =
						isSinglePayment && !flRecorrencia && row.digitableLine;
					const vParts = row.dtVencimento.split('/');
					const dueDate = `${vParts[2]}-${vParts[1]}-${vParts[0]}`;
					const rParts = row.dtVencimento.split('/');
					const referenceMonth = `${rParts[2]}/${rParts[1]}`;
					return {
						id: row.cdObrigacao,
						amount: {
							value: Number(removeDecimalPoint(row.vlAtualizado ?? row.vlObrigacao)),
						},
						dueDate,
						description: invoicePayment,
						referenceMonth,
						...(shouldIncludeDigitableLine ? {digitableLine: row.digitableLine} : {}),
						...(isRecurrenceEnabledAndHealth && index === 0
							? {
									recurrence: {
										partnerAccountId: beneficiary?.nuContrato ?? '',
										personId: beneficiary?.nuCpfC ?? '',
										extraParams: {
											serviceName: beneficiary?.nmPlano ?? '',
											serviceType: CheckoutRecurrenceEnum.SERVICE_TYPE,
											subchannel: SubChannelEnum.PORTAL_DO_BENEFICIARIO,
											person_id_type: 'CPF',
										},
									},
								}
							: {}),
						extraParams: {
							interestAmount: removeDecimalPoint(
								row.extraParams?.interestAmount || '0',
							),
							fineAmount: removeDecimalPoint(row.extraParams?.fineAmount || '0'),
							discountAmount: removeDecimalPoint(
								row.extraParams?.discountAmount || '0',
							),
						},
					};
				}),
				paymentOrder: {
					optins: {
						allowRecurrenceOptIn: isRecurrenceEnabledAndHealth ? true : false,
					},
				},
				'x-context': xContext,
				corporate,
			};
			checkoutPayment(payload, {
				onSuccess: data => {
					setCheckoutUrl(data.content.url);
					setIsIframeOpen(true);
					onSettled?.();
				},
				onError: () => {
					setIsPayError(true);
					onSettled?.();
				},
			});
		},
		[
			checkoutPayment,
			rows,
			selectedIds,
			beneficiary,
			currentEmail,
			currentPhone,
			flRecorrencia,
			isRecurrenceEnabledAndHealth,
			xContext,
		],
	);

	const handlePay = useCallback(() => {
		if (selectedIds.size === 0) {
			setNoSelectionModalOpen(true);
			return;
		}
		if (oldestOverdueBill && !selectedIds.has(oldestOverdueBill.cdObrigacao)) {
			const firstSelected = rows.find(r => selectedIds.has(r.cdObrigacao)) ?? null;
			openOlderBillsModal(firstSelected ?? rows[0], selectedIds.size > 1);
			return;
		}
		executePay();
	}, [selectedIds, oldestOverdueBill, rows, openOlderBillsModal, executePay]);

	const handlePayFromDrawer = useCallback(() => {
		if (
			oldestOverdueBill &&
			selectedRow &&
			selectedRow.cdObrigacao !== oldestOverdueBill.cdObrigacao
		) {
			openOlderBillsModal(selectedRow);
			return;
		}
		executePay(selectedRow ? [selectedRow] : undefined, handleCloseDrawer);
	}, [selectedRow, oldestOverdueBill, openOlderBillsModal, handleCloseDrawer, executePay]);

	const handleGoToOldestBill = useCallback(() => {
		setOlderBillsModalOpen(false);
		handleCloseDrawer();
		const overdueIds = new Set(
			rows.filter(r => r.status === OpenBillStatusEnum.OVERDUE).map(r => r.cdObrigacao),
		);
		handleSelectionChange(overdueIds);
	}, [rows, handleSelectionChange, handleCloseDrawer]);

	const handleContinueWithCurrent = useCallback(
		(bill: OpenBillRow) => {
			setOlderBillsModalOpen(false);
			setIsMultiSelectionOlderBills(false);
			if (isMultiSelectionOlderBills) {
				executePay();
			} else {
				executePay([bill]);
			}
		},
		[executePay, isMultiSelectionOlderBills],
	);

	return {
		autoPaymentType,
		rows,
		newBillsFlagIsEnabled,
		isMobile,
		isLoading,
		isError,
		isPayLoading,
		isPayError,
		noSelectionModalOpen,
		olderBillsModalOpen,
		olderBillsCurrentRow,
		oldestOverdueBill,
		isMultiSelectionOlderBills,
		numberOfOverdueBills,
		isEmpty,
		tabs,
		drawerOpen,
		selectedRow,
		selectedIds,
		hasPendingInitialDrawer,
		handleSelectionChange,
		handleToggleRow,
		handleViewDetails,
		handleCloseDrawer,
		handlePay,
		handlePayFromDrawer,
		handleGoToOldestBill,
		handleContinueWithCurrent,
		handleTryAgain: refetch,
		handleAutoPaymentEnable,
		handleAutoPaymentChange,
		handleAutoPaymentDeactivate,
		isAutoPaymentLoading,
		setIsPayError,
		setNoSelectionModalOpen,
		setOlderBillsModalOpen,
		checkoutUrl,
		isIframeOpen,
		setIsIframeOpen,
		recurrenceUrl,
		isRecurrenceIframeOpen,
		setIsRecurrenceIframeOpen: handleSetIsRecurrenceIframeOpen,
	};
};
